import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Copy } from "lucide-react";
import type { ChatMessageEffect } from "@/lib/chatApi";
import { playChatEcho, playChatImpact } from "@/lib/chatNotificationSound";

type SendEffectsMenuProps = {
  open: boolean;
  disabled?: boolean;
  onPick: (effect: ChatMessageEffect) => void;
  onClose: () => void;
};

/** Menu flottant au-dessus du bouton Envoyer (appui long). */
export function ChatSendEffectsMenu({ open, disabled, onPick, onClose }: SendEffectsMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("[data-chat-send-effects]")) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-chat-send-effects
      className="chat-send-effects absolute bottom-[calc(100%+0.45rem)] right-0 z-40 min-w-[12rem] overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
      role="menu"
    >
      <p className="border-b border-[var(--chat-border)] px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-[var(--chat-muted)]">
        Envoyer avec effet
      </p>
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-[var(--chat-surface-elevated)] disabled:opacity-40"
        onClick={() => onPick("impact")}
      >
        <span className="text-base" aria-hidden>
          💥
        </span>
        <span>
          <span className="block font-medium">Impact</span>
          <span className="block text-[10px] text-[var(--chat-muted)]">
            Slam + strobe 2× + tremblement
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-[var(--chat-surface-elevated)] disabled:opacity-40"
        onClick={() => onPick("echo")}
      >
        <span className="chat-effect-echo-chip" aria-hidden>
          <Copy className="size-4" strokeWidth={2.25} />
        </span>
        <span>
          <span className="block font-medium">Écho</span>
          <span className="block text-[10px] text-[var(--chat-muted)]">
            Copies du message plein écran
          </span>
        </span>
      </button>
    </div>
  );
}

function preferReduceMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

let shakeTimer: number | null = null;
let windupClearTimer: number | null = null;
let impactPrimedAt = 0;
let echoPrimedAt = 0;

/** Pic du whoosh (~788 ms) − 1er slam visuel (~160 ms). Écho aligné. */
export const IMPACT_SLAM_DELAY_MS = 620;
export const ECHO_BURST_DELAY_MS = IMPACT_SLAM_DELAY_MS;

function threadFxRoot(): HTMLElement | null {
  return (
    (document.querySelector("[data-chat-thread-fx]") as HTMLElement | null) ??
    (document.querySelector(".chat-app") as HTMLElement | null)
  );
}

export function beginImpactWindup(): void {
  const root = threadFxRoot();
  if (!root) return;
  root.classList.add("chat-thread--impact-windup");
  if (windupClearTimer != null) window.clearTimeout(windupClearTimer);
  windupClearTimer = null;
}

export function endImpactWindup(): void {
  const root = threadFxRoot();
  if (windupClearTimer != null) {
    window.clearTimeout(windupClearTimer);
    windupClearTimer = null;
  }
  impactPrimedAt = 0;
  echoPrimedAt = 0;
  root?.classList.remove("chat-thread--impact-windup");
}

/** Clic Impact : assombrit + lance le son tout de suite (le slam suit le pic). */
export function primeChatImpact(): void {
  playChatImpact();
  beginImpactWindup();
  impactPrimedAt = Date.now();
}

/** Clic Écho : même voile noir, rattle tout de suite, copies après le délai. */
export function primeChatEcho(): void {
  playChatEcho();
  beginImpactWindup();
  echoPrimedAt = Date.now();
}

function consumeDelay(startedAt: number, fallbackMs: number): number {
  if (!startedAt) return fallbackMs;
  return Math.max(0, fallbackMs - (Date.now() - startedAt));
}

function consumeImpactSlamDelay(): number {
  const started = impactPrimedAt;
  impactPrimedAt = 0;
  return consumeDelay(started, IMPACT_SLAM_DELAY_MS);
}

function consumeEchoBurstDelay(): number {
  const started = echoPrimedAt;
  echoPrimedAt = 0;
  return consumeDelay(started, ECHO_BURST_DELAY_MS);
}

function shakeChatThread() {
  const root = threadFxRoot();
  if (!root) return;
  root.classList.remove("chat-app--impact-shake");
  root.style.transform = "";
  void root.offsetWidth;
  root.classList.add("chat-app--impact-shake");
  if (shakeTimer != null) window.clearTimeout(shakeTimer);
  shakeTimer = window.setTimeout(() => {
    root.classList.remove("chat-app--impact-shake");
    root.style.transform = "";
    shakeTimer = null;
  }, 700);
}

function threadFxPortalTarget(): Element {
  return document.querySelector("[data-chat-thread-fx]") ?? document.body;
}

type EchoParticle = {
  id: number;
  left: string;
  top: string;
  delay: string;
  duration: string;
  scale: number;
  rotate: number;
};

function buildEchoParticles(count = 26): EchoParticle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${4 + Math.random() * 92}%`,
    top: `${6 + Math.random() * 88}%`,
    delay: `${Math.random() * 0.45}s`,
    duration: `${1.05 + Math.random() * 0.85}s`,
    scale: 0.55 + Math.random() * 0.9,
    rotate: -28 + Math.random() * 56,
  }));
}

function echoPreview(text: string): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= 36) return t;
  return `${t.slice(0, 34)}…`;
}

type EffectBubbleProps = {
  messageId: number;
  effect: ChatMessageEffect;
  mine: boolean;
  /** Texte pour l’effet Écho (duplication plein écran). */
  echoText?: string;
  children: ReactNode;
  playToken: number;
  onReplay: () => void;
};

/**
 * Impact / Écho — anim temporaire uniquement.
 * Lecture auto seulement via playToken (live / rejouer), jamais à l’historique.
 */
export function ChatEffectBubbleWrap({
  effect,
  mine,
  echoText = "",
  children,
  playToken,
  onReplay,
}: EffectBubbleProps) {
  const [playing, setPlaying] = useState(false);
  const [flash, setFlash] = useState(false);
  const [echoParticles, setEchoParticles] = useState<EchoParticle[] | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const trigger = (withScreenFx: boolean) => {
    if (effect === "impact" && withScreenFx && !preferReduceMotion()) {
      if (!impactPrimedAt) {
        playChatImpact();
        beginImpactWindup();
        impactPrimedAt = Date.now();
      }
      const wait = consumeImpactSlamDelay();
      window.setTimeout(() => {
        setPlaying(true);
        setFlash(true);
        shakeChatThread();
        window.setTimeout(() => setFlash(false), 340);
        if (windupClearTimer != null) window.clearTimeout(windupClearTimer);
        windupClearTimer = window.setTimeout(() => {
          endImpactWindup();
        }, 420);
      }, wait);
      return;
    }
    if (effect === "echo" && withScreenFx && !preferReduceMotion()) {
      if (!echoPrimedAt) {
        playChatEcho();
        beginImpactWindup();
        echoPrimedAt = Date.now();
      }
      const wait = consumeEchoBurstDelay();
      window.setTimeout(() => {
        setPlaying(true);
        const particles = buildEchoParticles();
        setEchoParticles(particles);
        window.setTimeout(() => setEchoParticles(null), 2100);
        if (windupClearTimer != null) window.clearTimeout(windupClearTimer);
        windupClearTimer = window.setTimeout(() => {
          endImpactWindup();
        }, 420);
      }, wait);
      return;
    }
    setPlaying(true);
    if (!withScreenFx || preferReduceMotion()) return;
    if (effect === "impact") {
      setFlash(true);
      shakeChatThread();
      window.setTimeout(() => setFlash(false), 340);
    } else if (effect === "echo") {
      playChatEcho();
      const particles = buildEchoParticles();
      setEchoParticles(particles);
      window.setTimeout(() => setEchoParticles(null), 2100);
    }
  };

  useEffect(() => {
    if (playToken <= 0) return;
    trigger(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playToken]);

  const onAnimEnd = () => {
    setPlaying(false);
  };

  const animClass = playing
    ? effect === "impact"
      ? "chat-effect-stage--impact"
      : effect === "echo"
        ? "chat-effect-stage--echo"
        : ""
    : "";

  const label = echoPreview(echoText);
  const echoMine = mine;

  return (
    <>
      {flash
        ? createPortal(
            <div className="chat-impact-flash" aria-hidden />,
            threadFxPortalTarget()
          )
        : null}
      {echoParticles && label
        ? createPortal(
            <div className="chat-echo-shower" aria-hidden>
              {echoParticles.map((p) => (
                <span
                  key={p.id}
                  className={`chat-echo-shower__particle ${
                    echoMine ? "chat-echo-shower__particle--mine" : "chat-echo-shower__particle--other"
                  }`}
                  style={
                    {
                      left: p.left,
                      top: p.top,
                      animationDelay: p.delay,
                      animationDuration: p.duration,
                      ["--echo-scale"]: p.scale,
                      ["--echo-rot"]: `${p.rotate}deg`,
                    } as CSSProperties
                  }
                >
                  {label}
                </span>
              ))}
            </div>,
            threadFxPortalTarget()
          )
        : null}
      <div className="chat-effect-wrap relative">
        <div
          ref={stageRef}
          className={`chat-effect-stage ${animClass} ${
            mine ? "chat-effect-stage--mine" : "chat-effect-stage--other"
          }`}
          onAnimationEnd={onAnimEnd}
        >
          {children}
        </div>
        <button
          type="button"
          className={`chat-effect-replay ${
            mine ? "chat-effect-replay--mine" : "chat-effect-replay--other"
          }`}
          title="Rejouer l’effet"
          aria-label="Rejouer l’effet"
          onClick={(e) => {
            e.stopPropagation();
            onReplay();
          }}
        >
          {effect === "impact" ? (
            <span aria-hidden>💥</span>
          ) : (
            <Copy className="chat-effect-echo size-3" strokeWidth={2.5} aria-hidden />
          )}
        </button>
      </div>
    </>
  );
}
